import {
  Badge,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack
} from '@mui/material'
import React from 'react'
import { ReactQueryDevtools } from 'react-query/devtools'
import { Link, useNavigate } from 'react-router-dom'

import Icon from 'components/Icon'
import { DrawerGremlinLogoLink } from 'components/navigation/Nav/DrawerGremlinLogoLink'
import { DrawerUserInfo } from 'components/navigation/Nav/DrawerUserInfo'
import { NavItemConfig } from 'components/navigation/Nav/use-gremlin-navigation'
import { useActiveReliabilityTests } from 'hooks/use-active-reliability-tests'
import useAttackResource from 'hooks/use-attack-resource'
import { useFeatureFlags } from 'hooks/use-feature-flags'
import { colors } from 'mui-theme/theme'
import theme from 'theme'

const wideDrawerWidth = 200
const narrowDrawerWidth = 80

const navBackgroundColor = colors.primary[900]

export const NavDrawer: React.FC<{
  children: React.ReactNode
  hasFaultInjection: boolean
  navigationConfigItems: NavItemConfig[]
}> = ({ children, hasFaultInjection, navigationConfigItems }) => {
  const navigate = useNavigate()
  const { nowRunning: isNowRunningEnabled } = useFeatureFlags()

  /**
   * Get active attacks, scenarios, and reliability tests
   */
  const { activeAttacksCount } = useAttackResource({
    completed: false,
    enabled: hasFaultInjection
  })
  const { activeReliabilityTests } = useActiveReliabilityTests()

  const totalActiveTests = activeAttacksCount + activeReliabilityTests.length

  const [anchorElement, setAnchorElement] = React.useState<null | HTMLElement>(
    null
  )
  const [selectedNavItem, setSelectedNavItem] = React.useState(null)

  const isSubnavOpen = Boolean(anchorElement)
  const handleClick = (
    event: React.MouseEvent<HTMLDivElement>,
    { hasSubnav, name, route, state }
  ) => {
    setSelectedNavItem(name)

    if (hasSubnav && !anchorElement) {
      setAnchorElement(event.currentTarget)
    } else if (route) {
      navigate(route, { state })
    }
  }

  const handleClose = () => setAnchorElement(null)

  const enabledNavigationConfigItems = navigationConfigItems.filter(
    (navItemConfig) => {
      // Special case for now-running nav item
      // TODO: Remove this special case once the feature flag is removed
      if (navItemConfig.name === 'now-running') {
        return navItemConfig.enabled && isNowRunningEnabled
      }

      return navItemConfig.enabled
    }
  )
  const breakpoint = theme.mq[1]

  return (
    <Box
      sx={{
        display: 'flex'
      }}
    >
      <nav
        aria-label="main-navigation"
        css={{
          background: colors.primary[900],
          zIndex: theme.zIndices[5]
        }}
      >
        <Drawer
          css={[
            {
              width: narrowDrawerWidth,
              flexShrink: 0,
              whiteSpace: 'normal',
              boxSizing: 'border-box',
              transition: 'width 1s',

              [breakpoint]: {
                width: wideDrawerWidth,
                transition: 'width 1s'
              }
            }
          ]}
          id="NavDrawer"
          open={true}
          sx={{
            '& .MuiDrawer-paper': {
              background: navBackgroundColor,
              width: narrowDrawerWidth,
              overflowX: 'hidden',
              transition: 'width 1s',

              [breakpoint]: {
                width: wideDrawerWidth,
                transition: 'width 1s'
              }
            }
          }}
          variant="permanent"
        >
          <Stack
            css={{
              display: 'flex',
              flexDirection: 'column',
              flex: '1 1 auto'
            }}
          >
            <DrawerGremlinLogoLink
              url={hasFaultInjection ? '/' : '/services'}
            />
            <List
              css={{
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1 auto'
              }}
            >
              {enabledNavigationConfigItems.map((navItem) => {
                // since component is either button or Link, only provide one of these
                const clickProps = navItem.subnav
                  ? {
                      onClick: (e) =>
                        handleClick(e, {
                          hasSubnav: Boolean(navItem.subnav),
                          name: navItem.name,
                          route: navItem.route,
                          state: navItem.state
                        })
                    }
                  : { to: navItem.route, state: navItem.state }

                const isListIconSpinning =
                  navItem.name === 'now-running' && totalActiveTests > 0

                return (
                  <ListItem
                    key={navItem.text}
                    disablePadding
                    css={{
                      ...navItem.style
                    }}
                    secondaryAction={
                      navItem.showBadgeCount ? (
                        <Badge
                          showZero
                          badgeContent={totalActiveTests}
                          color={totalActiveTests > 0 ? 'warning' : 'secondary'}
                        />
                      ) : null
                    }
                  >
                    <ListItemButton
                      component={navItem.subnav ? 'button' : Link}
                      css={[
                        {
                          cursor: navItem.disabled ? 'not-allowed' : 'pointer',
                          color: navItem.disabled
                            ? theme.colors.steel[1]
                            : theme.colors.white[0],
                          flexDirection: 'column',
                          justifyContent: 'center',
                          paddingTop: theme.space[2],
                          paddingBottom: theme.space[2],
                          paddingLeft: theme.space[1],
                          paddingRight: theme.space[1],
                          svg: {
                            opacity: 0.5,
                            color: theme.colors.white[0]
                          },

                          [breakpoint]: {
                            flexDirection: 'row',
                            justifyContent: 'initial'
                          },

                          '&:hover, &:focus': {
                            color: navItem.disabled
                              ? theme.colors.steel[1]
                              : theme.colors.white[0],
                            svg: {
                              opacity: 1,
                              color: colors.secondary.blue[100]
                            }
                          }
                        },

                        navItem.showNotification && {
                          position: 'relative',
                          '&::before': {
                            content: "''",
                            position: 'absolute',
                            top: theme.space[2],
                            right: theme.space[3],
                            width: theme.space[2],
                            height: theme.space[2],
                            borderRadius: theme.borderRadius[2],
                            background: theme.colors.yellow[0],
                            border: `2px solid ${theme.colors.steel[2]}`,
                            zIndex: 2
                          }
                        },

                        navItem.isActive && {
                          color: theme.colors.white[0],
                          background: colors.primary[650],
                          svg: {
                            opacity: 1,
                            color: theme.colors.white[0]
                          },

                          // this is needed to override the mui styling defaults
                          '&:hover, &:focus': {
                            background: colors.primary[650]
                          },

                          '&:hover': {
                            svg: {
                              color: colors.secondary.blue[100]
                            }
                          }
                        }
                      ]}
                      data-link-active={navItem.isActive}
                      data-testid={`nav-${navItem.name}`}
                      data-track-action={`open-${navItem.name}`}
                      data-track-category="main-navigation"
                      data-track-on="click"
                      disabled={navItem.disabled}
                      {...clickProps}
                      {...(navItem.props || {})}
                    >
                      <ListItemIcon
                        sx={{
                          justifyContent: 'center',
                          color: navItem.disabled
                            ? theme.colors.steel[1]
                            : theme.colors.white[0]
                        }}
                      >
                        <Icon
                          css={[
                            {
                              width: theme.fontSizes[4],
                              height: theme.fontSizes[4],
                              transition: 'opacity 150ms ease 0s'
                            }
                          ]}
                          data-test-navigation-notification={
                            navItem.showNotification
                          }
                          icon={navItem.icon}
                          spinning={isListIconSpinning}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={navItem.text}
                        primaryTypographyProps={{ variant: 'body2' }}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontWeight: theme.fontWeights[0],
                            lineHeight: `${theme.space[3]}px`,
                            textAlign: 'center',
                            fontSize: theme.fontSizes[0],

                            [breakpoint]: {
                              textAlign: 'left',
                              fontSize: theme.fontSizes[1]
                            }
                          }
                        }}
                      />
                      {navItem.subnav && (
                        <>
                          <ListItemIcon
                            css={{
                              display: 'none',
                              [breakpoint]: {
                                display: 'flex',
                                justifyContent: 'center'
                              }
                            }}
                          >
                            <Icon icon="chevronRight" />
                          </ListItemIcon>

                          {navItem.name === selectedNavItem && (
                            <Menu
                              anchorEl={anchorElement}
                              anchorOrigin={{
                                vertical: 'center',
                                horizontal: 'right'
                              }}
                              open={isSubnavOpen}
                              transformOrigin={{
                                vertical: 'center',
                                horizontal: 'left'
                              }}
                              onClose={handleClose}
                            >
                              {navItem.subnav.map((subnavItem) => (
                                <MenuItem
                                  key={subnavItem.name}
                                  component={Link}
                                  css={[
                                    {
                                      '&:hover': {
                                        backgroundColor: 'transparent'
                                      }
                                    }
                                  ]}
                                  to={subnavItem.route}
                                  onClick={() => {
                                    setAnchorElement(null)
                                    handleClose()
                                  }}
                                >
                                  <ListItemText
                                    primary={subnavItem.name}
                                    primaryTypographyProps={{
                                      variant: 'body2'
                                    }}
                                  />
                                </MenuItem>
                              ))}
                            </Menu>
                          )}
                        </>
                      )}
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
            {process.env.NODE_ENV === 'development' && !window.Cypress && (
              <ReactQueryDevtools initialIsOpen={false} />
            )}

            <DrawerUserInfo
              css={{
                display: 'none',
                [breakpoint]: {
                  display: 'unset'
                }
              }}
            />
          </Stack>
        </Drawer>
      </nav>
      {children}
    </Box>
  )
}
